import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Building2,
  ArrowLeft,
  CreditCard,
  Heart,
  Home,
  Zap,
  ExternalLink,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LocalServices = ({ user }) => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState(user?.target_country || "ireland");
  const [services, setServices] = useState(null);
  const [loading, setLoading] = useState(true);

  const serviceCategories = [
    { key: "banking", label: "Banking", icon: CreditCard, color: "#4A6C6F" },
    { key: "healthcare", label: "Healthcare", icon: Heart, color: "#E53E3E" },
    { key: "housing", label: "Housing", icon: Home, color: "#48BB78" },
    { key: "utilities", label: "Utilities", icon: Zap, color: "#ED8936" },
  ];

  useEffect(() => {
    fetchServices();
  }, [selectedCountry]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/services/${selectedCountry}`, {
        credentials: "include",
      });
      if (response.ok) {
        setServices(await response.json());
      }
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-[#E2E8F0] px-4 lg:px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              className="text-[#4A5568]"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-[#4A6C6F] rounded-full flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading text-xl font-semibold text-[#1A202C]">Local Services</span>
            </div>
          </div>
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger data-testid="country-select" className="w-48 bg-white border-[#E2E8F0]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ireland">🇮🇪 Ireland</SelectItem>
              <SelectItem value="uk">🇬🇧 United Kingdom</SelectItem>
              <SelectItem value="canada">🇨🇦 Canada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-8 py-8">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-12 h-12 border-4 border-[#4A6C6F] border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : services ? (
          <Tabs defaultValue="banking" className="space-y-6">
            <TabsList className="bg-[#F5F5F0] p-1 rounded-full flex-wrap">
              {serviceCategories.map((cat) => (
                <TabsTrigger
                  key={cat.key}
                  data-testid={`tab-${cat.key}`}
                  value={cat.key}
                  className="rounded-full px-4 data-[state=active]:bg-[#4A6C6F] data-[state=active]:text-white"
                >
                  <cat.icon className="w-4 h-4 mr-2" />
                  {cat.label}
                </TabsTrigger>
              ))}
            </TabsList>

            {serviceCategories.map((cat) => (
              <TabsContent key={cat.key} value={cat.key} className="space-y-4">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services[cat.key]?.map((service, index) => (
                    <Card
                      key={index}
                      data-testid={`service-${cat.key}-${index}`}
                      className="bg-white border-0 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] rounded-2xl hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1)] transition-shadow"
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div
                            className="w-12 h-12 rounded-xl flex items-center justify-center"
                            style={{ backgroundColor: `${cat.color}15` }}
                          >
                            <cat.icon className="w-6 h-6" style={{ color: cat.color }} />
                          </div>
                          <a
                            href={service.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#4A6C6F] hover:text-[#3A5658] transition-colors"
                          >
                            <ExternalLink className="w-5 h-5" />
                          </a>
                        </div>
                        <h3 className="font-heading text-lg font-semibold text-[#1A202C] mb-2">
                          {service.name}
                        </h3>
                        <p className="text-[#4A5568] text-sm">{service.description}</p>
                        <a
                          href={service.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 mt-4 text-sm text-[#4A6C6F] hover:text-[#3A5658] font-medium transition-colors"
                        >
                          Visit Website
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        ) : (
          <div className="text-center py-12 text-[#718096]">
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No services available for this country</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default LocalServices;
